import { useState, type ReactNode } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { AnimatePresence, motion } from 'motion/react';
import { GLOSSARY, type GlossaryTermId } from '@allemnii-pulse/shared';

/**
 * The single tooltip primitive for every term that needs clarification (TDD, PRD, Blocked, On
 * Hold, Out of scope, ...) — see docs/PLAN.md §4. Reads from the shared glossary so the
 * definition is written once and stays consistent everywhere it's used. Built on Radix's
 * Tooltip primitive for positioning/a11y, with the fade driven by motion.dev (forceMount so
 * AnimatePresence can play the exit).
 */
export function Term({ id, children }: { id: GlossaryTermId; children?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const entry = GLOSSARY[id];

  return (
    <TooltipPrimitive.Provider delayDuration={150}>
      <TooltipPrimitive.Root open={open} onOpenChange={setOpen}>
        <TooltipPrimitive.Trigger asChild>
          <span
            tabIndex={0}
            className="cursor-help border-b border-dotted border-accent outline-none"
          >
            {children ?? entry.term}
          </span>
        </TooltipPrimitive.Trigger>
        <AnimatePresence>
          {open && (
            <TooltipPrimitive.Portal forceMount>
              <TooltipPrimitive.Content asChild sideOffset={6} className="z-50">
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.12 }}
                  className="w-64 rounded-lg bg-[var(--ink-900)] p-3 text-xs leading-relaxed text-[var(--ink-50)] shadow-lg"
                >
                  <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-accent">
                    {entry.term}
                  </span>
                  {entry.definition}
                </motion.div>
              </TooltipPrimitive.Content>
            </TooltipPrimitive.Portal>
          )}
        </AnimatePresence>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
